import AHBoolean from "common/values/boolean/boolean";
import Date from "common/values/date/date";
import Guid from "common/values/guid/guid";
import Percent from "common/values/percent/percent";
import _ from "lodash";
import Individual, { IndividualJSON } from "marketplace/entities/individual/individual";
import User, { UserJSON } from "users/entities/user/user";
import Session from "users/session/session";
import EntityClientRepresentative from "work/entities/entity-client-representative/entity-client-representative";
import EntityRepresentative, { EntityRepresentativeJSON } from "work/entities/entity-representative/entity-representative";
import EntityVendorRepresentative from "work/entities/entity-vendor-representative/entity-vendor-representative";
import WorkAgreement, { IWorkAgreementStore, Masked, MaskedContracts, MaskedDiscount, MaskedFeeSchedule, MaskedField, MaskedTeam, WorkAgreementJSON } from "../work-agreement/work-agreement";
import { ProposalAction, ProposalFieldName, ProposalStatus } from "work/values/constants";
import ProposalReviewer, { ProposalReviewerJSON } from "work/values/proposal-reviewer";
import UserInfo, { UserInfoJSON } from "work/values/user-info/user-info";
import ProjectDescription from "work/values/project-description/project-description";
import ProjectName from "work/values/project-name/project-name";
import ProposalRedline, { ProposalRedlineJSON, RedlinedDates, RedlinedDocuments } from "./redlining/proposal-redline";
import VendorTeam from "work/values/vendor-team/vendor-team";
import DetailedVendorTeam, { DetailedVendorTeamJSON } from "work/values/vendor-team/detailed-vendor-team";
import FeeScheduleCategory from "work/values/fee-schedule-category/fee-schedule-category";
import WorkDocument from "work/values/work-document/work-document";
import ProposalBuilder from "./utils/proposal-builder";
import FieldRedline, { FieldRedlineArray, IRedlineableField } from "./redlining/field-redline";
import { FeeScheduleCategoryMaskedCurrency } from "../../values/fee-schedule-category/fee-schedule-category";
import { Audience } from "./proposal-forum-topic-context";
import { FeeScheduleRedline } from "./redlining/fee-schedule-redline/fee-schedule-redline";
import Subscriber from "messaging/values/subcriber/subscriber";


export default class Proposal {
  public replacedBy?: Guid = undefined;
  private readonly _RFPId?: Guid;
  private readonly _proposalStore: IProposalStore;
  private readonly _workAgreementStore: IWorkAgreementStore;
  private readonly _id?: Guid;
  private readonly _name?: ProjectName;
  private readonly _description?: ProjectDescription;
  private readonly _creator?: EntityRepresentative;
  private readonly _creatorInfo?: UserInfo;
  private readonly _createdDate?: Date;
  private readonly _lastUpdated?: Date;
  private readonly _workAgreement: WorkAgreement;
  private readonly _negotiable: boolean = true;
  private readonly _responseDueBy?: Date;
  private readonly _status: ProposalStatus = ProposalStatus.AwaitingSubmission;
  private readonly _availableActions: { [key in ProposalAction]: Guid[] };
  private readonly _details?: ProposalDetails;
  private readonly _currentUser: User;
  private _redlining?: ProposalRedline;
  protected _originalProposal: Proposal;
  private _lastSavedRedline: ProposalRedline | undefined;

  constructor(
    proposalStore: IProposalStore,
    workStore: IWorkAgreementStore,
    currentUser: User,
    spec: CompleteProposalSpec,
    originalProposal?: Proposal,
    metaInfo?: ProposalMetaInfo,
    details?: ProposalDetails,
    withRedline: boolean = true
  ) {
    this._proposalStore = proposalStore;
    this._workAgreementStore = workStore;
    this._currentUser = currentUser;

    this._name = spec.name;
    this._description = spec.description;

    const workAgreement = new WorkAgreement(workStore);
    workAgreement.id = metaInfo?.workAgreementId;
    workAgreement.RFPId = metaInfo?.RFPId;
    workAgreement.client = spec.client;
    workAgreement.vendors = spec.vendors;
    workAgreement.name = spec.name;
    workAgreement.description = spec.description;
    workAgreement.startDate = spec.startDate;
    workAgreement.endDate = spec.endDate;
    workAgreement.team = spec.vendorTeam;
    workAgreement.teamRestricted = spec.teamRestricted;
    workAgreement.feeSchedule = spec.feeSchedule ?? [];
    workAgreement.clientPolicyDocuments = spec.clientPolicyDocuments ?? [];
    workAgreement.vendorPolicyDocuments = spec.vendorPolicyDocuments ?? [];
    workAgreement.conflictsDocuments = spec.conflictsDocuments ?? [];
    workAgreement.clientTeamTemplateIds = spec.clientTeamTemplateIds ?? [];
    workAgreement.vendorTeamTemplateIds = spec.vendorTeamTemplateIds ?? [];
    workAgreement.clientFeeScheduleTemplateIds =
      spec.clientFeeScheduleTemplateIds ?? [];
    workAgreement.vendorFeeScheduleTemplateIds =
      spec.vendorFeeScheduleTemplateIds ?? [];
    workAgreement.conflictsCheckWaived =
      spec.conflictsCheckWaived ?? new AHBoolean(false);
    workAgreement.discount = spec.discount ?? new Percent(0);
    workAgreement.archiveDate = metaInfo?.archiveDate;
    workAgreement.hireDate = metaInfo?.hireDate;

    this._workAgreement = workAgreement;

    this._negotiable = spec.negotiable;
    this._responseDueBy = spec.responseDueBy;
    this._clientReviewers = spec.clientReviewers ?? [];
    this._vendorReviewers = spec.vendorReviewers ?? [];

    this._RFPId = metaInfo?.RFPId;
    this._id = metaInfo?.id;
    this._createdDate = metaInfo?.createdDate;
    this._lastUpdated = metaInfo?.lastUpdated;
    this._creator = metaInfo?.creator;
    this._creatorInfo = metaInfo?.creatorInfo;
    this._supersededById = metaInfo?.supersededById ?? null;
    this._status = metaInfo?.status ?? ProposalStatus.AwaitingSubmission;
    this._availableActions = metaInfo?.availableActions ?? {
      [ProposalAction.Submit]: [],
      [ProposalAction.Approve]: [],
      [ProposalAction.Reject]: [],
      [ProposalAction.Revise]: [],
      [ProposalAction.Hire]: [],
      [ProposalAction.Delete]: [],
      [ProposalAction.Cancel]: [],
      [ProposalAction.Edit]: [],
      [ProposalAction.Review]: [],
    };
    this.replacedBy = metaInfo?.replacedBy;
    this._supersedes = details?.supersedes;
    this._details = details;
    if (withRedline && this.isSubmitted) {
      this.createRedline(this.details?.redlining);
    }
    this._originalProposal = originalProposal ?? this;
  }

  private readonly _supersedes?: Proposal;

  public get supersedes(): Proposal | null {
    return this._supersedes ?? null;
  }

  private readonly _supersededById: Guid | null;

  public get supersededById(): Guid | null {
    return this._supersededById ?? null;
  }

  protected _clientReviewers: ProposalReviewer[] = [];

  public get clientReviewers(): ProposalReviewer[] {
    return [...this._clientReviewers];
  }

  protected _vendorReviewers: ProposalReviewer[] = [];

  public get vendorReviewers(): ProposalReviewer[] {
    return [...this._vendorReviewers];
  }

  public get RFPId(): Guid | undefined {
    return this._RFPId;
  }

  public get id(): Guid | null {
    return this._id ? this._id?.clone() : null;
  }

  public get creator(): EntityRepresentative | null {
    return this._creator?.clone() ?? null;
  }

  public get creatorInfo(): UserInfo | null {
    return this._creatorInfo?.clone() ?? null;
  }

  public get createdDate(): Date | null {
    return this._createdDate?.clone() ?? null;
  }

  public get lastUpdated(): Date | null {
    return this._lastUpdated?.clone() ?? null;
  }

  public get status(): ProposalStatus {
    return this._status;
  }

  public get isApprovedByBothParties(): boolean {
    return [
      ProposalStatus.AwaitingApprovalByTeam,
      ProposalStatus.AwaitingHire,
      ProposalStatus.AwaitingApprovalByVendors,
    ].includes(this.status);
  }

  public get name(): ProjectName | undefined {
    return this._name?.clone();
  }

  public get description(): ProjectDescription | undefined {
    return this._description?.clone();
  }

  public get negotiable(): boolean {
    return this._negotiable;
  }

  public get responseDueBy(): Date | undefined {
    return this._responseDueBy?.clone();
  }

  public get reviewersKey(): string {
    const reviewerKeys: string[] = [];
    this._clientReviewers.forEach((reviewer) => {
      reviewerKeys.push(`client-${reviewer.userId.value}-${reviewer.canEdit}`);
    });

    this._vendorReviewers.forEach((reviewer) => {
      reviewerKeys.push(`vendor-${reviewer.userId.value}-${reviewer.canEdit}`);
    });
    return reviewerKeys.toSorted((a, b) => a.localeCompare(b)).join(",");
  }

  public get areReviewersModified(): boolean {
    return (
      !_.isEqual(
        this.clientReviewers,
        this._originalProposal.clientReviewers
      ) ||
      !_.isEqual(
        this.vendorReviewers,
        this._originalProposal.vendorReviewers
      )
    );
  }

  public get client(): EntityClientRepresentative | null {
    return this._workAgreement.client?.clone() ?? null;
  }

  public get vendors(): EntityVendorRepresentative[] | null {
    return this._workAgreement.vendors.map((vendor) => vendor.clone()) ?? null;
  }

  public get vendorTeam(): VendorTeam | DetailedVendorTeam | MaskedTeam | null {
    return this._workAgreement.team?.clone() ?? null;
  }

  public set vendorTeam(team: VendorTeam | DetailedVendorTeam | MaskedTeam | null) {
    this._workAgreement.team = team?.clone();
  }

  public get teamRestricted(): AHBoolean | MaskedTeam {
    return this._workAgreement.teamRestricted?.clone() ?? new AHBoolean(false);
  }

  public get feeSchedule(): FeeScheduleCategory[] | FeeScheduleCategoryMaskedCurrency[] | MaskedFeeSchedule {
    if (Masked.isMasked(this._workAgreement.feeSchedule)) {
      return this._workAgreement.feeSchedule;
    }
    return (
      this._workAgreement.feeSchedule?.map((category) => category.clone()) ?? []
    );
  }

  public get startDate(): Date | undefined {
    return this._workAgreement.startDate?.clone();
  }

  public get endDate(): Date | undefined {
    return this._workAgreement.endDate?.clone();
  }

  public get discount(): Percent | MaskedDiscount {
    return this._workAgreement?.discount ?? new Percent(0);
  }

  public get clientPolicyDocuments(): WorkDocument[] | MaskedContracts {
    if (Masked.isMasked(this._workAgreement.clientPolicyDocuments)) {
      return this._workAgreement.clientPolicyDocuments;
    }
    return this._workAgreement.clientPolicyDocuments.map((doc) => doc.clone());
  }

  public get vendorPolicyDocuments(): WorkDocument[] | MaskedContracts {
    if (Masked.isMasked(this._workAgreement.vendorPolicyDocuments)) {
      return this._workAgreement.vendorPolicyDocuments;
    }
    return this._workAgreement.vendorPolicyDocuments.map((doc) => doc.clone());
  }

  public get conflictsDocuments(): WorkDocument[] | MaskedContracts {
    if (Masked.isMasked(this._workAgreement.conflictsDocuments)) {
      return this._workAgreement.conflictsDocuments;
    }
    return this._workAgreement.conflictsDocuments.map((doc) => doc.clone());
  }

  public get clientTeamTemplateIds(): Guid[] {
    const templates = this._workAgreement.clientTeamTemplateIds;
    return templates.map((id) => id.clone());
  }

  public get vendorTeamTemplateIds(): Guid[] {
    const templates = this._workAgreement.vendorTeamTemplateIds;
    return templates.map((id) => id.clone());
  }

  public get clientFeeScheduleTemplateIds(): Guid[] {
    const templates = this._workAgreement.clientFeeScheduleTemplateIds;
    return templates.map((id) => id.clone());
  }

  public get vendorFeeScheduleTemplateIds(): Guid[] {
    const templates = this._workAgreement.vendorFeeScheduleTemplateIds;
    return templates.map((id) => id.clone());
  }

  public get conflictsCheckWaived(): AHBoolean | MaskedContracts {
    return this._workAgreement.conflictsCheckWaived ?? new AHBoolean(false);
  }

  public get availableActions(): { [key in ProposalAction]: Guid[] } {
    return this._availableActions;
  }

  public get details(): ProposalDetails | undefined {
    return this._details;
  }

  public get isArchived(): boolean {
    return this._status === ProposalStatus.Archived;
  }

  public get archiveDate(): Date | undefined {
    return this._workAgreement.archiveDate?.clone();
  }

  public get isSubmitted(): boolean {
    return this._status !== ProposalStatus.AwaitingSubmission;
  }

  public get isHired(): boolean {
    return this._workAgreement.hireDate !== undefined;
  }

  public get hireDate(): Date | undefined {
    return this._workAgreement.hireDate?.clone();
  }

  public get workAgreementId(): Guid | undefined {
    return this._workAgreement.id?.clone();
  }

  public get spec(): CompleteProposalSpec {
    const workAgreement = this._workAgreement.clone();

    return {
      client: workAgreement.client,
      name: this._name?.clone(),
      description: this._description?.clone(),
      negotiable: this._negotiable,
      responseDueBy: this._responseDueBy?.clone() ?? undefined,
      clientReviewers: this.clientReviewers.map((reviewer) => reviewer.clone()),
      vendorReviewers: this.vendorReviewers.map((reviewer) => reviewer.clone()),
      vendors: workAgreement.vendors,
      teamRestricted: Masked.isMasked(workAgreement.teamRestricted) ? new MaskedTeam() :
        workAgreement.teamRestricted?.clone() ?? new AHBoolean(false),
      feeSchedule: Masked.isMasked(workAgreement.feeSchedule) ? new MaskedTeam() :
        workAgreement.feeSchedule ?? [],
      startDate: workAgreement.startDate?.clone(),
      endDate: workAgreement.endDate?.clone(),
      vendorTeam: Masked.isMasked(workAgreement.team) ? new MaskedTeam() :
        workAgreement.team?.clone() as DetailedVendorTeam ?? undefined,
      conflictsCheckWaived: Masked.isMasked(workAgreement.conflictsCheckWaived) ? new MaskedContracts() :
        workAgreement.conflictsCheckWaived?.clone() ?? new AHBoolean(false),
      conflictsDocuments: Masked.isMasked(workAgreement.conflictsDocuments) ? new MaskedContracts() :
        workAgreement.conflictsDocuments,
      clientPolicyDocuments: Masked.isMasked(workAgreement.clientPolicyDocuments) ? new MaskedContracts() :
        workAgreement.clientPolicyDocuments,
      vendorPolicyDocuments: Masked.isMasked(workAgreement.vendorPolicyDocuments) ? new MaskedContracts() :
        workAgreement.vendorPolicyDocuments,
      clientTeamTemplateIds: workAgreement.clientTeamTemplateIds,
      vendorTeamTemplateIds: workAgreement.vendorTeamTemplateIds,
      clientFeeScheduleTemplateIds: workAgreement.clientFeeScheduleTemplateIds,
      vendorFeeScheduleTemplateIds: workAgreement.vendorFeeScheduleTemplateIds,
      discount: Masked.isMasked(workAgreement.discount) ? new MaskedDiscount() :
        workAgreement.discount ?? new Percent(0),
    };
  }

  public get metaInfo(): ProposalMetaInfo {
    if (!this._id || !this._createdDate || !this._creator)
      throw new Error(
        "Cannot get meta info for a proposal that has not been saved."
      );
    return {
      RFPId: this._RFPId,
      id: this._id?.clone(),
      workAgreementId: this._workAgreement.id?.clone(),
      supersededById: this._supersededById?.clone(),
      createdDate: this._createdDate.clone(),
      lastUpdated: this._lastUpdated?.clone() ?? this._createdDate.clone(),
      creator: this._creator?.clone(),
      creatorInfo: this._creatorInfo?.clone(),
      status: this._status,
      availableActions: this._availableActions,
      replacedBy: this.replacedBy,
      archiveDate: this._workAgreement.archiveDate?.clone(),
      hireDate: this._workAgreement.hireDate?.clone(),
    };
  }

  public get redline(): ProposalRedline | undefined {
    return this._redlining ?? undefined;
  }

  public get userIsLatestSubmittingParty(): boolean {
    if (this.creator?.userId.isEqualTo(this._currentUser.id)) return true;

    const creatorIsClient = this.client?.userId.isEqualTo(this.creator?.userId);
    if (creatorIsClient) {
      return this._clientReviewers.some((reviewer) =>
        reviewer.userId.isEqualTo(this._currentUser.id));
    }

    if (Masked.isMasked(this.vendorTeam)) return false;
    const creatorIsTeamLeader = this.vendorTeam?.leader?.userId.isEqualTo(this.creator?.userId);
    if (creatorIsTeamLeader) {
      return this._vendorReviewers.some((reviewer) =>
        reviewer.userId.isEqualTo(this._currentUser.id));
    }
    return false;
  };

  public get isModified(): boolean {
    if (this.hasUnsavedRedlineChanges) {
      return true;
    }
    let reviewersModified = false;
    if (this.client && this._currentUser.id?.isEqualTo((this.client.userId))) {
      reviewersModified = this._clientReviewers.length !== this._originalProposal.clientReviewers.length ||
        this._clientReviewers.some(
          (latestReviewer) =>
            !this._originalProposal.clientReviewers.some(
              (originalReviewer) => originalReviewer.isEqualTo(latestReviewer)))
    } else if (
      this.vendorTeam &&
      Masked.isNotMasked(this.vendorTeam) &&
      this._currentUser.id?.isEqualTo((this.vendorTeam.leader?.userId))) {
      reviewersModified = this._vendorReviewers.length !== this._originalProposal.vendorReviewers.length ||
        this._vendorReviewers.some((latestReviewer) =>
          !this._originalProposal.vendorReviewers.some((originalReviewer) =>
            originalReviewer.isEqualTo(latestReviewer)))
    }
    return !this.isEquivalentTo(this._originalProposal) || reviewersModified;
  }

  public get redlinedRevision(): Proposal {
    if (!this._redlining?.isResolved)
      throw new Error("Cannot load an unresolved redline revision.");
    const revisionSpec = this.spec;

    if (this._redlining.name.currentEntry) {
      revisionSpec.name = this._redlining.name.currentEntry;
    }

    if (this._redlining.description.currentEntry) {
      revisionSpec.description = this._redlining.description.currentEntry;
    }

    revisionSpec.responseDueBy =
      this._redlining.responseDueBy.currentEntry?.clone();
    revisionSpec.startDate = this._redlining.startDate.currentEntry?.clone();
    revisionSpec.endDate = this._redlining.endDate.currentEntry?.clone();

    if (Masked.isNotMasked(this.vendorTeam) && !this.vendorTeam?.leader) {
      throw new Error("Cannot create a redlined revision without a vendor team.");
    } else if (
      Masked.isMasked(this.vendorTeam) ||
      Masked.isMasked(this._redlining.team) ||
      Masked.isMasked(this._redlining.teamRestricted)
    ) {
      revisionSpec.vendorTeam = this.vendorTeam as MaskedTeam;
      revisionSpec.teamRestricted = this.teamRestricted;
    } else {
      const teamMembers: Individual[] = []
      for (const teamRedline of this._redlining.team.redlines) {
        if (!teamRedline.currentEntry) continue;
        teamMembers.push(teamRedline.currentEntry);
        revisionSpec.vendorTeam = new DetailedVendorTeam(
          this.vendorTeam.leader as Individual,
          teamMembers
        )
      }
      revisionSpec.teamRestricted = this._redlining.teamRestricted.currentEntry ?? new AHBoolean(false);
    }

    if (Masked.isMasked(this._redlining.feeSchedule)) {
      revisionSpec.feeSchedule = this.feeSchedule;
    } else {
      revisionSpec.feeSchedule = [];
      for (const redlineCategory of this._redlining.feeSchedule.redlines) {
        if (
          !redlineCategory.currentEntry ||
          redlineCategory.currentEntry instanceof FeeScheduleCategoryMaskedCurrency
        ) continue;
        revisionSpec.feeSchedule.push(redlineCategory.currentEntry);
      }
    }

    if (
      Masked.isMasked(this._redlining.conflictsCheckWaived) ||
      Masked.isMasked(this._redlining.conflictsDocuments) ||
      Masked.isMasked(this._redlining.clientPolicyDocuments) ||
      Masked.isMasked(this._redlining.vendorPolicyDocuments)) {
      revisionSpec.conflictsCheckWaived = this.conflictsCheckWaived as MaskedContracts;
      revisionSpec.conflictsDocuments = this.conflictsDocuments as MaskedContracts;
      revisionSpec.clientPolicyDocuments = this.clientPolicyDocuments as MaskedContracts;
      revisionSpec.vendorPolicyDocuments = this.clientPolicyDocuments as MaskedContracts;
    } else {
      revisionSpec.conflictsCheckWaived =
        this._redlining.conflictsCheckWaived.currentEntry ?? new AHBoolean(false);
      revisionSpec.conflictsDocuments =
        this._redlining.conflictsDocuments.redlines
          .filter((redline) => redline.currentEntry)
          .map((redline) => redline.currentEntry) as WorkDocument[];
      revisionSpec.clientPolicyDocuments =
        this._redlining.clientPolicyDocuments.redlines
          .filter((redline) => redline.currentEntry)
          .map((redline) => redline.currentEntry) as WorkDocument[];
      revisionSpec.vendorPolicyDocuments =
        this._redlining.vendorPolicyDocuments.redlines
          .filter((redline) => redline.currentEntry)
          .map((redline) => redline.currentEntry) as WorkDocument[];
    }

    revisionSpec.discount = Masked.isMasked(this._redlining.discount) ? this._redlining.discount :
      this._redlining.discount.currentEntry ?? new Percent(0);

    const revision = new Proposal(
      this._proposalStore,
      this._workAgreementStore,
      this._currentUser,
      revisionSpec,
      this._originalProposal,
      this.metaInfo,
      this.details,
      false
    );
    revision._clientReviewers = this._clientReviewers;
    revision._vendorReviewers = this._vendorReviewers;
    revision._redlining = this._redlining?.clone();
    revision._workAgreement.clientTeamTemplateIds = this.clientTeamTemplateIds;
    revision._workAgreement.vendorTeamTemplateIds = this.vendorTeamTemplateIds;
    revision._workAgreement.clientFeeScheduleTemplateIds = this.clientFeeScheduleTemplateIds;
    revision._workAgreement.vendorFeeScheduleTemplateIds = this.vendorFeeScheduleTemplateIds;
    return revision;
  }

  public clone(): Proposal {
    const clonedProposal = new Proposal(
      this._proposalStore,
      this._workAgreementStore,
      this._currentUser,
      this.spec,
      this._originalProposal,
      this.metaInfo,
      this.details,
      false
    );
    clonedProposal._originalProposal = this._originalProposal || this;
    clonedProposal._redlining = this._redlining?.clone();
    clonedProposal._lastSavedRedline = this._lastSavedRedline?.clone();
    return clonedProposal;
  }

  public hasFeeCategory(category: FeeScheduleCategory): boolean {
    if (Masked.isMasked(this._workAgreement.feeSchedule)) return false;
    return (
      this._workAgreement.feeSchedule?.some((cat) =>
        cat.name?.isEqualTo(category.name)
      ) ?? false
    );
  }

  public isAssociatedWithUserId(id: Guid): boolean {
    if (this._creator?.userId.isEqualTo(id)) return true;
    if (this._clientReviewers.some((reviewer) => reviewer.userId.isEqualTo(id)))
      return true;
    if (this._vendorReviewers.some((reviewer) => reviewer.userId.isEqualTo(id)))
      return true;
    if (this._status !== ProposalStatus.AwaitingSubmission) {
      if (this._workAgreement.client?.userId.isEqualTo(id)) return true;
      if (Masked.isMasked(this._workAgreement.team)) return false;
      if (this._workAgreement.team?.leader?.userId.isEqualTo(id)) return true;
      if (
        this._workAgreement.team?.members.some((member) =>
          member.userId.isEqualTo(id)
        )
      )
        return true;
    }
    return false;
  }

  public getReviewer(userId?: Guid): ProposalReviewer | undefined {
    if (!userId) return undefined;
    return (
      this._clientReviewers.find((reviewer) =>
        reviewer.userId.isEqualTo(userId)
      ) ||
      this._vendorReviewers.find((reviewer) =>
        reviewer.userId.isEqualTo(userId)
      )
    );
  }

  public isModifiedByBuilder(builder?: ProposalBuilder): boolean {
    if (!builder?.currentSpec) return false;

    const currentSpec = builder.currentSpec;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const checkEquality = (a: any, b: any) =>
      (!a && b) || (a && !a.isEqualTo?.(b));

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const checkArrayEquality = (a: any[], b: any[]) =>
      a.length !== b.length || !a.every(item => b.some(bItem => bItem.isEqualTo?.(item)));

    const fieldsToCheck = [
      [
        currentSpec.teamRestricted,
        this.teamRestricted
      ],
      [
        currentSpec.team,
        this.vendorTeam
      ],
      [
        currentSpec.name,
        this.name
      ],
      [
        currentSpec.description,
        this.description
      ],
      [
        currentSpec.responseDueBy,
        this.responseDueBy
      ],
      [
        currentSpec.startDate,
        this.startDate
      ],
      [
        currentSpec.endDate,
        this.endDate
      ],
      [
        currentSpec.conflictsCheckWaived,
        this.conflictsCheckWaived
      ],
      [
        currentSpec.discount,
        this.discount
      ],
    ];

    for (const [specField, thisField] of fieldsToCheck) {
      if (checkEquality(
        specField,
        thisField
      )) return true;
    }

    if (
      Masked.isNotMasked(currentSpec.feeSchedule) &&
      Masked.isNotMasked(this.feeSchedule) &&
      (currentSpec.feeSchedule.length !== this.feeSchedule.length ||
        !currentSpec.feeSchedule.every(currentCategory =>
          Array.isArray(this.feeSchedule) &&
          this.feeSchedule.some((category) => category.isEqualTo(currentCategory))))
    ) {
      return true;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const arrayChecks: [any[], any[]][] = [
      [
        currentSpec.clientTeamTemplateIds,
        this.clientTeamTemplateIds
      ],
      [
        currentSpec.vendorTeamTemplateIds,
        this.vendorTeamTemplateIds
      ],
      [
        currentSpec.clientFeeScheduleTemplateIds,
        this.clientFeeScheduleTemplateIds
      ],
      [
        currentSpec.vendorFeeScheduleTemplateIds,
        this.vendorFeeScheduleTemplateIds
      ],
      [
        currentSpec.clientReviewers ?? [],
        this.clientReviewers
      ],
      [
        currentSpec.vendorReviewers ?? [],
        this.vendorReviewers
      ],
    ];

    if (Masked.isNotMasked(currentSpec.clientPolicyDocuments) &&
      Masked.isNotMasked(this.clientPolicyDocuments)
    ) {
      arrayChecks.push([
        currentSpec.clientPolicyDocuments,
        this.clientPolicyDocuments
      ]);
    }

    if (Masked.isNotMasked(currentSpec.vendorPolicyDocuments) &&
      Masked.isNotMasked(this.vendorPolicyDocuments)
    ) {
      arrayChecks.push([
        currentSpec.vendorPolicyDocuments,
        this.vendorPolicyDocuments
      ]);
    }

    if (Masked.isNotMasked(currentSpec.conflictsDocuments) &&
      Masked.isNotMasked(this.conflictsDocuments)) {
      arrayChecks.push([
        currentSpec.conflictsDocuments,
        this.conflictsDocuments
      ]);
    }

    for (const [specArray, thisArray] of arrayChecks) {
      if (checkArrayEquality(
        specArray,
        thisArray
      )) return true;
    }
    return false;
  }

  public async save(session: Readonly<Session>, proposalBuilderJSON?: object):
    Promise<{
      proposal: Proposal,
      userProposalBuilder?: ProposalBuilder,
      userRedlining?: ProposalRedline
    }> {
    if (this._id !== undefined) {
      if (!this._originalProposal) {
        throw new Error(
          "Cannot save a proposal without the original proposal."
        );
      }

      const proposalResponse = await this._proposalStore.saveProposal(
        this,
        proposalBuilderJSON
      );
      if (proposalResponse.proposal) {
        proposalResponse.proposal.updateLastSavedRedline();
      }
      return proposalResponse;
    } else {
      const savedProposal = await this._proposalStore.createProposal(
        this,
        session
      );
      return {
        proposal: savedProposal
      }
    }
  }

  public async share(session: Readonly<Session>):
    Promise<Proposal> {
    if (this._id !== undefined
    ) {
      if (!this._originalProposal) {
        throw new Error(
          "Cannot save a proposal without the original proposal."
        );
      }
      return await this._proposalStore.shareProposal(
        this._originalProposal,
        this
      );
    } else {
      return await this._proposalStore.createProposal(
        this,
        session
      );
    }
  }

  public async submit(userId?: Guid): Promise<{
    proposal: Proposal,
    userProposalBuilder?: ProposalBuilder,
    userRedlining?: ProposalRedline
  }> {
    if (!
      userId
    ) {
      throw new Error("Cannot submit a proposal without a user id.");
    }
    if (!this.creator?.userId.isEqualTo(userId)) {
      throw new Error("Cannot submit a proposal that you did not create.");
    }
    if (this._status !== ProposalStatus.AwaitingSubmission) {
      throw new Error(
        "Cannot submit a proposal that has already been submitted."
      );
    }
    if (this._workAgreement.team === undefined) {
      throw new Error("Cannot submit a proposal without a vendor-team.");
    }

    return await this._proposalStore.submitProposal(this);
  }

  public async approve(userId: Guid, session: Readonly<Session>): Promise<{
    proposal: Proposal,
    userProposalBuilder?: ProposalBuilder,
    userRedlining?: ProposalRedline
  }> {
    if (!userId) {
      throw new Error("Cannot approve a proposal without a user id.");
    }
    if (!this.isAssociatedWithUserId(userId)) {
      throw new Error(
        "Cannot approve a proposal that you are not associated with."
      );
    }

    if (
      this.status !== ProposalStatus.AwaitingApprovalByClient &&
      this.status !== ProposalStatus.AwaitingApprovalByTeam &&
      this.status !== ProposalStatus.AwaitingApprovalByTeamLeader &&
      this.status !== ProposalStatus.AwaitingApprovalByVendors
    ) {
      throw new Error(
        "Cannot approve a proposal that is not awaiting approval."
      );
    }

    if (!this._workAgreement.team) {
      throw new Error("Cannot approve a proposal without a vendor-team.");
    }

    if (Masked.isMasked(this._workAgreement.feeSchedule)) {
      throw new Error("Cannot approve a proposal with a masked fee schedule.");
    }
    if (
      !this._workAgreement.feeSchedule ||
      this._workAgreement.feeSchedule.length === 0
    ) {
      throw new Error("Cannot approve a proposal without a fee schedule.");
    }
    if (this._workAgreement.feeSchedule.some((category) =>
      category instanceof FeeScheduleCategory &&
      !category.fee
    )) {
      throw new Error("Cannot approve a proposal with deferred fees.");
    }

    if (Masked.isMasked(this._workAgreement.conflictsCheckWaived) ||
      Masked.isMasked(this._workAgreement.conflictsDocuments) ||
      Masked.isMasked(this._workAgreement.clientPolicyDocuments) ||
      Masked.isMasked(this._workAgreement.vendorPolicyDocuments)
    ) {
      throw new Error("Cannot approve a proposal with masked contracts.");
    }

    if ((!this._workAgreement.conflictsCheckWaived.value &&
      this._workAgreement.conflictsDocuments.length === 0)
    ) {
      throw new Error(
        "Cannot approve a proposal without a conflicts check or waiver."
      );
    }
    return await this._proposalStore.approveProposal(this, session);
  }

  public async reject(userId?: Guid): Promise<{
    proposal: Proposal,
    userProposalBuilder?: ProposalBuilder,
    userRedlining?: ProposalRedline
  }> {
    if (!
      userId
    ) {
      throw new Error("Cannot reject a proposal without a user id.");
    }
    if (!this.isAssociatedWithUserId(userId)) {
      throw new Error(
        "Cannot reject a proposal that you are not associated with."
      );
    }
    if (!this.isSubmitted || this.isArchived) {
      throw new Error(
        "Cannot reject a proposal that is not awaiting approval."
      );
    }

    return await this._proposalStore.rejectProposal(this);
  }

  public async hire(userId?: Guid): Promise<{
    proposal: Proposal,
    userProposalBuilder?: ProposalBuilder,
    userRedlining?: ProposalRedline
  }> {
    if (!
      userId
    ) {
      throw new Error("Cannot hire a proposal without a user id.");
    }
    if (this._workAgreement.hireDate !== undefined) {
      throw new Error("Cannot hire a proposal that has already been hired.");
    }
    if (this.status !== ProposalStatus.AwaitingHire) {
      throw new Error("Cannot hire a proposal that is not awaiting hire.");
    }

    return await this._proposalStore.hireProposal(this);
  }

  public async delete(userId?: Guid): Promise<void> {
    if (!
      userId
    ) {
      throw new Error("Cannot delete a proposal without a user id.");
    }
    if (this.creator?.userId.value !== userId.value) {
      throw new Error("Cannot delete a proposal that you did not create.");
    }
    if (this.status !== ProposalStatus.AwaitingSubmission) {
      throw new Error(
        "Cannot delete a proposal that has already been submitted."
      );
    }

    return await this._proposalStore.deleteProposal(this);
  }

  public async cancel(userId?: Guid): Promise<Proposal> {
    if (!userId) {
      throw new Error("Cannot cancel a proposal without a user id.");
    }
    if (this.creator?.userId.value !== userId.value) {
      throw new Error("Cannot cancel a proposal that you did not create.");
    }

    return await this._proposalStore.cancelProposal(this);
  }

  public userCanEdit(user?: User | null): boolean {
    if (!user?.id) return false;
    if (!this.id) return true;

    return this.availableActions[ProposalAction.Edit].some((id) =>
      id.isEqualTo(user.id)
    );
  }

  public userCanRevise(user?: User | null): boolean {
    if (!user?.id) return false;

    return this.availableActions[ProposalAction.Revise].some((id) =>
      id.isEqualTo(user.id)
    );
  }

  public userCanRedline(user?: User | null): boolean {
    if (!user?.id) return false;
    if (this.userCanRevise(user)) return true;

    return this.clientReviewers.some(r => r.canEdit && r.userId.isEqualTo(user.id)) ||
      this.vendorReviewers.some(r => r.canEdit && r.userId.isEqualTo(user.id));
  }


  userCanApprove(user?: User | null): boolean {
    if (!user?.id) return false;
    if (Masked.isMasked(this._workAgreement.conflictsCheckWaived) ||
      Masked.isMasked(this._workAgreement.conflictsDocuments) ||
      (!this._workAgreement.conflictsCheckWaived.value &&
        this._workAgreement.conflictsDocuments.length === 0)
    ) {
      return false;
    }

    return this.availableActions[ProposalAction.Approve].some((id) =>
      id.isEqualTo(user.id)
    );
  }

  public userCanReject(user?: User | null): boolean {
    if (!user?.id) return false;
    return this.availableActions[ProposalAction.Reject].some((id) =>
      id.isEqualTo(user.id)
    );
  }

  public userCanHire(user?: User | null): boolean {
    if (!user?.id) return false;
    return this.availableActions[ProposalAction.Hire].some((id) =>
      id.isEqualTo(user.id)
    );
  }

  public toJSON(
    depth: number = 1,
    originalProposalJSON?: ProposalJSON,
    supersedesJSON?: ProposalJSON,
    detailSupersedesJSON?: ProposalJSON
  ): ProposalJSON {
    if (!this._id) {
      throw new Error("Cannot serialize a proposal without an id.");
    }
    if (!this._creator) {
      throw new Error("Cannot serialize a proposal without a creator.");
    }

    // Depth-limited
    const originalProposal = depth > 0 && typeof this._originalProposal.toJSON === "function"
      ? this._originalProposal.toJSON(depth - 1, this._originalProposal.toJSON(0))
      : originalProposalJSON;

    // Depth-limited
    const supersedes = depth > 0 && typeof this._supersedes?.toJSON === "function"
      ? this._supersedes?.toJSON(depth - 1, originalProposal, this._supersedes?.toJSON(0))
      : supersedesJSON;

    // Depth-limited
    const detailSupersedes = depth > 0 && typeof this._details?.supersedes?.toJSON === "function"
      ? this._details?.supersedes?.toJSON(depth - 1, originalProposal, supersedesJSON, this._details?.supersedes?.toJSON(0))
      : detailSupersedesJSON;

    return {
      currentUser: this._currentUser.toJSON(),
      name: this._name?.value,
      description: this._description?.value,
      workAgreement: this._workAgreement.toJSON(),
      negotiable: this._negotiable,
      responseDueBy: this._responseDueBy?.toJSON(),
      clientReviewers: this._clientReviewers.map((reviewer) => reviewer.toJSON()),
      vendorReviewers: this._vendorReviewers.map((reviewer) => reviewer.toJSON()),
      RFPId: this._RFPId?.toJSON(),
      id: this._id?.toJSON(),
      createdDate: this._createdDate?.toJSON(),
      lastUpdated: this._lastUpdated?.toJSON(),
      creator: this._creator?.toJSON(),
      creatorInfo: this._creatorInfo?.toJSON(),
      status: this._status,
      availableActions: Object.fromEntries(
        Object.entries(this._availableActions).map(([action, ids]) => [
          action,
          ids.map((id) => id.toJSON())
        ])
      ),
      replacedBy: this.replacedBy?.toJSON(),
      supersedes: supersedes,
      details: {
        client: this._details?.client?.toJSON(),
        team: this._details?.vendorTeam?.toJSON(),
        redlining: this._details?.redlining?.toJSON(),
        redliningUpdated: this._details?.redliningUpdated?.toJSON(),
        supersedes: detailSupersedes,
        supersededById: this._details?.supersededById?.toJSON(),
      },
      redlining: this._redlining?.toJSON(),
      originalProposal: originalProposal,
      lastSavedRedline: this._lastSavedRedline?.toJSON(),
    };
  }

  public static fromJSON(
    proposalStore: IProposalStore,
    workStore: IWorkAgreementStore,
    json: ProposalJSON
  ): Proposal {
    if (!json.id) {
      throw new Error("Cannot load a proposal without an id.");
    }

    const workJson = json.workAgreement ?? json;
    const workAgreement = WorkAgreement.fromJSON(workStore, workJson);

    const spec: CompleteProposalSpec = {
      RFPId: workAgreement.RFPId,
      vendors: workAgreement.vendors,
      teamRestricted: workAgreement.teamRestricted,
      feeSchedule: workAgreement.feeSchedule ?? [],
      conflictsCheckWaived: workAgreement.conflictsCheckWaived,
      conflictsDocuments: workAgreement.conflictsDocuments ?? [],
      clientPolicyDocuments: workAgreement.clientPolicyDocuments ?? [],
      vendorPolicyDocuments: workAgreement.vendorPolicyDocuments ?? [],
      clientTeamTemplateIds: workAgreement.clientTeamTemplateIds ?? [],
      vendorTeamTemplateIds: workAgreement.vendorTeamTemplateIds ?? [],
      clientFeeScheduleTemplateIds: workAgreement.clientFeeScheduleTemplateIds ?? [],
      vendorFeeScheduleTemplateIds: workAgreement.vendorFeeScheduleTemplateIds ?? [],
      discount: workAgreement.discount ?? new Percent(0),
      vendorTeam: workAgreement.team as DetailedVendorTeam | undefined,
      client: workAgreement.client,
      name: json.name ? ProjectName.fromJSON(json.name) : workAgreement.name,
      description: json.description ? ProjectDescription.fromJSON(json.description) : workAgreement.description,
      negotiable: Boolean(json.negotiable),
      responseDueBy: Date.fromJSON(json.responseDueBy) ?? undefined,
      startDate: workAgreement.startDate,
      endDate: workAgreement.endDate,
      clientReviewers: (json.clientReviewers ?? []).map((r) => ProposalReviewer.fromJSON(r)),
      vendorReviewers: (json.vendorReviewers ?? []).map((r) => ProposalReviewer.fromJSON(r)),
    };

    const availableActions: { [key in ProposalAction]: Guid[] } = {
      [ProposalAction.Submit]: (json.availableActions?.Submit ?? []).map((id: string) => Guid.fromJSON(id)),
      [ProposalAction.Approve]: (json.availableActions?.Approve ?? []).map((id: string) => Guid.fromJSON(id)),
      [ProposalAction.Reject]: (json.availableActions?.Reject ?? []).map((id: string) => Guid.fromJSON(id)),
      [ProposalAction.Revise]: (json.availableActions?.Revise ?? []).map((id: string) => Guid.fromJSON(id)),
      [ProposalAction.Hire]: (json.availableActions?.Hire ?? []).map((id: string) => Guid.fromJSON(id)),
      [ProposalAction.Delete]: (json.availableActions?.Delete ?? []).map((id: string) => Guid.fromJSON(id)),
      [ProposalAction.Cancel]: (json.availableActions?.Cancel ?? []).map((id: string) => Guid.fromJSON(id)),
      [ProposalAction.Edit]: (json.availableActions?.Edit ?? []).map((id: string) => Guid.fromJSON(id)),
      [ProposalAction.Review]: (json.availableActions?.Review ?? []).map((id: string) => Guid.fromJSON(id)),
    };

    const creator = EntityVendorRepresentative.fromJSON(json.creator);
    if (!creator) throw new Error("No creator found");
    const metaInfo: ProposalMetaInfo = {
      RFPId: workAgreement.RFPId,
      id: Guid.fromJSON(json.id),
      workAgreementId: workAgreement.id,
      createdDate: Date.fromJSON(json.createdDate) ?? new Date(),
      lastUpdated: Date.fromJSON(json.lastUpdated) ?? new Date(),
      creator: creator,
      creatorInfo: json.creatorInfo ? UserInfo.fromJSON(json.creatorInfo) : undefined,
      status: json.status as ProposalStatus,
      supersededById: json.supersededById ? Guid.fromJSON(json.supersededById) : undefined,
      availableActions,
      replacedBy: json.replacedBy ? Guid.fromJSON(json.replacedBy) : undefined,
      hireDate: workAgreement.hireDate,
      archiveDate: workAgreement.archiveDate
    };

    const details: ProposalDetails | undefined = (() => {
      const maybeClient = json.details?.client;
      const maybeTeam = json.details?.team;
      const maybeRedlining = json.redlining ?? json.details?.redlining;

      let redlineObj: ProposalRedline | undefined;
      if (maybeRedlining !== undefined && maybeRedlining !== null) {
        redlineObj = ProposalRedline.fromJSON(maybeRedlining);
      }

      if (!maybeClient) return;

      let team: DetailedVendorTeam | MaskedTeam | undefined;

      if (maybeTeam && "isMasked" in maybeTeam) {
        team = new MaskedTeam();
      } else if (maybeTeam) {
        team = DetailedVendorTeam.fromJSON(maybeTeam);
      }

      const d: ProposalDetails = {
        client: Individual.fromJSON(maybeClient),
        vendorTeam: team,
        redlining: redlineObj,
        redliningUpdated: Date.fromJSON(json.details?.redliningUpdated) ?? undefined,
        supersedes: json.supersedes ? Proposal.fromJSON(proposalStore, workStore, json.supersedes) : undefined,
        supersededById: json.supersededById ? Guid.fromJSON(json.supersededById) : undefined,
      };

      // Only return details if we actually populated anything meaningful.
      if (d.client || d.vendorTeam || d.redlining || d.redliningUpdated || d.supersedes || d.supersededById) {
        return d;
      }
      return undefined;
    })();

    const proposal = new Proposal(
      proposalStore,
      workStore,
      User.fromJSON(json.currentUser),
      spec,
      json.originalProposal ? Proposal.fromJSON(
        proposalStore,
        workStore,
        json.originalProposal
      ) : undefined,
      metaInfo,
      details,
      true
    );

    // Preserve replacedBy if present (constructor copies from metaInfo already but keep explicit)
    proposal.replacedBy = metaInfo.replacedBy;

    return proposal;
  }

  public updateRedline(redline: ProposalRedline): Proposal {
    const newProposal = this.clone();
    newProposal._redlining = redline;
    return newProposal;
  }

  public updateClientTeamTemplateIds(ids: Guid[]
  ):
    Proposal {
    const newProposal = this.clone();
    newProposal._workAgreement.clientTeamTemplateIds = ids;
    return newProposal;
  }

  public updateVendorTeamTemplateIds(ids: Guid[]
  ):
    Proposal {
    const newProposal = this.clone();
    newProposal._workAgreement.vendorTeamTemplateIds = ids;
    return newProposal;
  }

  public updateClientFeeScheduleTemplateIds(ids: Guid[]
  ):
    Proposal {
    const newProposal = this.clone();
    newProposal._workAgreement.clientFeeScheduleTemplateIds = ids;
    return newProposal;
  }

  public updateVendorFeeScheduleTemplateIds(ids: Guid[]
  ):
    Proposal {
    const newProposal = this.clone();
    newProposal._workAgreement.vendorFeeScheduleTemplateIds = ids;
    return newProposal;
  }

  public updateClientReviewers(reviewers: ProposalReviewer[]
  ):
    Proposal {
    const newProposal = this.clone();
    newProposal._clientReviewers = reviewers;
    return newProposal;
  }

  public updateVendorReviewers(reviewers: ProposalReviewer[]
  ):
    Proposal {
    const newProposal = this.clone();
    newProposal._vendorReviewers = reviewers;
    return newProposal;
  }

  /**
   * Takes a redline shared between the same party (e.g. Client Reviewers + Client) and merges based on the original
   * state of the sharedRedline.
   *
   * This is so when a member of the same party saves redline changes, you do not lose your changes
   * unless it's a field they have also changed.
   * @param existingRedline the existing redline to merge
   * @param invert swap which redline takes precedence when both parties have changed the same field
   * @returns a new Proposal with the merged redline
   */
  public mergeSharedRedline(existingRedline: ProposalRedline, invert?: boolean): Proposal {
    if (!this._redlining) throw new Error("Cannot merge a redline without a redline.");
    const nameRedline = existingRedline.name.originalRedline.isEqualTo(this._redlining.name) || invert
      ? existingRedline.name
      : this._redlining.name;
    const descriptionRedline = existingRedline.description.originalRedline.isEqualTo(this._redlining.description) ||
      invert
      ? existingRedline.description : this._redlining.description;
    const responseDueByRedline = existingRedline.responseDueBy.originalRedline.isEqualTo(this._redlining.responseDueBy) ||
      invert
      ? existingRedline.responseDueBy : this._redlining.responseDueBy;
    const startDateRedline = existingRedline.startDate.originalRedline.isEqualTo(this._redlining.startDate) || invert
      ? existingRedline.startDate : this._redlining.startDate;
    const endDateRedline = existingRedline.endDate.originalRedline.isEqualTo(this._redlining.endDate) || invert
      ? existingRedline.endDate : this._redlining.endDate;

    let teamRestrictedRedline: FieldRedline<AHBoolean> | MaskedTeam;
    if (Masked.isMasked(this._redlining.teamRestricted) ||
      Masked.isMasked(existingRedline.teamRestricted)) {
      teamRestrictedRedline = this._redlining.teamRestricted as MaskedTeam
    } else {
      teamRestrictedRedline =
        existingRedline.teamRestricted.originalRedline.isEqualTo(this._redlining.teamRestricted) || invert ?
          existingRedline.teamRestricted :
          this._redlining.teamRestricted;
    }

    const teamRedline =
      (Masked.isMasked(this._redlining.team) ||
        Masked.isMasked(existingRedline.team)) ?
        this._redlining.team as MaskedTeam :
        this._redlining.team.mergeSharedRedlineArray(
          existingRedline.team,
        );

    const feeScheduleRedline =
      (Masked.isMasked(this._redlining.feeSchedule) ||
        Masked.isMasked(existingRedline.feeSchedule)) ?
        this._redlining.feeSchedule as MaskedFeeSchedule :
        this._redlining.feeSchedule.mergeSharedFeeScheduleRedline(
          existingRedline.feeSchedule
        );

    const conflictsDocumentsRedline =
      (Masked.isMasked(this._redlining.conflictsDocuments) ||
        Masked.isMasked(existingRedline.conflictsDocuments)) ?
        this._redlining.conflictsDocuments as MaskedContracts :
        this._redlining.conflictsDocuments.mergeSharedRedlineArray(
          existingRedline.conflictsDocuments,
        );

    const clientPolicyDocumentsRedline =
      (Masked.isMasked(this._redlining.clientPolicyDocuments) ||
        Masked.isMasked(existingRedline.clientPolicyDocuments)) ?
        this._redlining.clientPolicyDocuments as MaskedContracts :
        this._redlining.clientPolicyDocuments.mergeSharedRedlineArray(
          existingRedline.clientPolicyDocuments,
        );

    const vendorPolicyDocumentsRedline =
      (Masked.isMasked(this._redlining.vendorPolicyDocuments) ||
        Masked.isMasked(existingRedline.vendorPolicyDocuments)) ?
        this._redlining.vendorPolicyDocuments as MaskedContracts :
        this._redlining.vendorPolicyDocuments.mergeSharedRedlineArray(
          existingRedline.vendorPolicyDocuments
        );


    let conflictsCheckWaivedRedline: FieldRedline<AHBoolean> | MaskedContracts;
    if (Masked.isMasked(this._redlining.conflictsCheckWaived) ||
      Masked.isMasked(existingRedline.conflictsCheckWaived)) {
      conflictsCheckWaivedRedline = this._redlining.conflictsCheckWaived as MaskedContracts
    } else {
      conflictsCheckWaivedRedline =
        existingRedline.conflictsCheckWaived.originalRedline
          .isEqualTo(this._redlining.conflictsCheckWaived) || invert ?
          existingRedline.conflictsCheckWaived :
          this._redlining.conflictsCheckWaived;
    }

    let discountRedline: FieldRedline<Percent> | MaskedDiscount;
    if (Masked.isMasked(this._redlining.discount) ||
      Masked.isMasked(existingRedline.discount)) {
      discountRedline = this._redlining.discount as MaskedDiscount
    } else {
      discountRedline = existingRedline.discount.originalRedline
        .isEqualTo(this._redlining.discount) || invert
        ? existingRedline.discount : this._redlining.discount;
    }

    const mergedRedline = new ProposalRedline(
      nameRedline,
      descriptionRedline,
      {
        responseDueBy: responseDueByRedline,
        startDate: startDateRedline,
        endDate: endDateRedline
      },
      teamRedline,
      feeScheduleRedline,
      conflictsCheckWaivedRedline,
      teamRestrictedRedline,
      {
        conflictsDocuments: conflictsDocumentsRedline,
        clientPolicyDocuments: clientPolicyDocumentsRedline,
        vendorPolicyDocuments: vendorPolicyDocumentsRedline
      },
      discountRedline,
      new Date()
    );
    return this.updateRedline(mergedRedline);
  }

  public async requestRevision(session: Readonly<Session>): Promise<{
    proposal: Proposal,
    userProposalBuilder?: ProposalBuilder,
    userRedlining?: ProposalRedline
  }> {
    return await this._proposalStore.requestRevision(this, session);
  }

  public getCommentAudienceAndSubscribers(isExternal: boolean, session: Session): {
    audience: Audience;
    subscribers: Subscriber[]
  } {
    let audience: Audience;
    const subscribers: Subscriber[] = [];
    if (session.user?.id) subscribers.push(new Subscriber(session.user.id, session.entities[0].id));

    if (isExternal) {
      audience = Audience.AllReviewers;
      if (this.creator?.userId) subscribers.push(new Subscriber(this.creator.userId, this.creator.entityId));
      if (this.client?.userId) subscribers.push(new Subscriber(this.client.userId, this.client.entityId));
      if (Masked.isNotMasked(this.vendorTeam) && this.vendorTeam?.leader) {
        subscribers.push(new Subscriber(this.vendorTeam.leader.userId, this.vendorTeam.leader.entityId!));
      }

      subscribers.push(
        ...this.clientReviewers.map((reviewer) => new Subscriber(reviewer.userId, reviewer.entityId)),
        ...this.vendorReviewers.map((reviewer) => new Subscriber(reviewer.userId, reviewer.entityId))
      );
    } else if (session.context?.viewingAsVendor) {
      audience = Audience.VendorReviewers;
      if (Masked.isNotMasked(this.vendorTeam) &&
        this.vendorTeam?.leader?.userId
      ) {
        subscribers.push(new Subscriber(this.vendorTeam.leader.userId, this.vendorTeam.leader.entityId!));
      }

      subscribers.push(
        ...this.vendorReviewers.map((reviewer) => new Subscriber(reviewer.userId, reviewer.entityId))
      );
    } else {
      audience = Audience.ClientReviewers;
      if (this.client?.userId) subscribers.push(new Subscriber(this.client.userId, this.client.entityId));
      subscribers.push(
        ...this.clientReviewers.map((reviewer) => new Subscriber(reviewer.userId, reviewer.entityId))
      );
    }

    return {
      audience,
      subscribers: _.uniqBy(
        subscribers,
        (subscriber) => subscriber.userId.value
      ),
    };
  }

  public isEquivalentTo(other: Proposal): boolean {
    if (Masked.isNotMasked(this.vendorTeam) && !this.vendorTeam?.isEqualTo(other.vendorTeam)) {
      return false;
    }

    if (
      Masked.isNotMasked(this.teamRestricted) &&
      Masked.isNotMasked(other.teamRestricted) &&
      !this.teamRestricted.isEqualTo(other.teamRestricted)
    ) return false;

    if ((Masked.isNotMasked(this._workAgreement.feeSchedule) && Masked.isNotMasked(other.feeSchedule)) &&
      (this._workAgreement.feeSchedule?.length !== other.feeSchedule.length ||
        !this._workAgreement.feeSchedule?.every((category, index) =>
          (Masked.isNotMasked(other.feeSchedule) && category.isEqualTo(other.feeSchedule[index])))))
      return false;

    if (Masked.isNotMasked(this.conflictsCheckWaived) && Masked.isNotMasked(other.conflictsCheckWaived) &&
      (!this.conflictsCheckWaived.isEqualTo(other.conflictsCheckWaived)))
      return false;

    if ((Masked.isNotMasked(this._workAgreement.conflictsDocuments) && Masked.isNotMasked(other.conflictsDocuments)) &&
      (this._workAgreement.conflictsDocuments?.length !== other.conflictsDocuments.length ||
        !this._workAgreement.conflictsDocuments?.every((category, index) =>
          (Masked.isNotMasked(other.conflictsDocuments) && category.isEqualTo(other.conflictsDocuments[index])))))
      return false;

    if ((Masked.isNotMasked(this._workAgreement.clientPolicyDocuments) && Masked.isNotMasked(other.clientPolicyDocuments)) &&
      (this._workAgreement.clientPolicyDocuments?.length !== other.clientPolicyDocuments.length ||
        !this._workAgreement.clientPolicyDocuments?.every((category, index) =>
          (Masked.isNotMasked(other.clientPolicyDocuments) && category.isEqualTo(other.clientPolicyDocuments[index])))))
      return false;

    if ((Masked.isNotMasked(this._workAgreement.vendorPolicyDocuments) && Masked.isNotMasked(other.vendorPolicyDocuments)) &&
      (this._workAgreement.vendorPolicyDocuments?.length !== other.vendorPolicyDocuments.length ||
        !this._workAgreement.vendorPolicyDocuments?.every((category, index) =>
          (Masked.isNotMasked(other.vendorPolicyDocuments) && category.isEqualTo(other.vendorPolicyDocuments[index])))))
      return false;

    if (Masked.isNotMasked(this.discount) && Masked.isNotMasked(other.discount) &&
      !this.discount.isEqualTo(other.discount))
      return false;

    return this.client?.isEqualTo(other.client) &&
      this.name?.isEqualTo(other.name) &&
      this.description?.isEqualTo(other.description) &&
      this.negotiable === other.negotiable &&
      ((!this.responseDueBy && !other.responseDueBy) || this.responseDueBy?.isEqualTo(other.responseDueBy)) &&
      ((!this.startDate && !other.startDate) || this.startDate?.isEqualTo(other.startDate)) &&
      ((!this.endDate && !other.endDate) || this.endDate?.isEqualTo(other.endDate)) || false;
  }

  public createRedline(
    originalRedline?: ProposalRedline
  ): void {
    if (!
      this.id || !this.name || !this.description || !this.details
    )
      return;
    let previousVersion = this._supersedes ?? this;
    if (this.isApprovedByBothParties) {
      previousVersion = this.clone();
    }

    const nameRedline = this.createFieldRedline(
      ProposalField.Name,
      previousVersion.name ?? null,
      this.name,
      ProjectName.Prototype,
      true,
      originalRedline?.name,
    )
    const descriptionRedline = this.createFieldRedline(
      ProposalField.Description,
      previousVersion.description ?? null,
      this.description,
      ProjectDescription.Prototype,
      true,
      originalRedline?.description
    );
    const teamRedline =
      Masked.isMasked(originalRedline?.team) ? originalRedline.team :
        this.createTeamRedline(previousVersion.details?.vendorTeam, originalRedline?.team)

    const feeScheduleRedline =
      (Masked.isMasked(originalRedline?.feeSchedule) ||
        Masked.isMasked(previousVersion.feeSchedule)) ?
        new MaskedFeeSchedule() :
        this.createFeeScheduleRedline(
          previousVersion.feeSchedule,
          originalRedline?.feeSchedule
        );

    const teamRestrictedRedline =
      (Masked.isMasked(this.teamRestricted) ||
        Masked.isMasked(originalRedline?.teamRestricted) ||
        Masked.isMasked(previousVersion.teamRestricted)) ?
        previousVersion.teamRestricted as MaskedTeam :
        this.createFieldRedline(
          ProposalField.TeamRestriction,
          previousVersion.teamRestricted ?? null,
          this.teamRestricted,
          AHBoolean.Prototype,
          false,
          originalRedline?.teamRestricted
        );

    const conflictsCheckWaivedRedline =
      (Masked.isMasked(this.conflictsCheckWaived) ||
        Masked.isMasked(originalRedline?.conflictsCheckWaived) ||
        Masked.isMasked(previousVersion.conflictsCheckWaived)) ?
        previousVersion.conflictsCheckWaived as MaskedContracts :
        this.createFieldRedline(
          ProposalField.WaiveConflictsCheck,
          previousVersion.conflictsCheckWaived ?? null,
          this.conflictsCheckWaived,
          AHBoolean.Prototype,
          false,
          originalRedline?.conflictsCheckWaived
        );


    const documentsRedline: RedlinedDocuments = {
      clientPolicyDocuments:
        (Masked.isMasked(this.clientPolicyDocuments) ||
          Masked.isMasked(originalRedline?.clientPolicyDocuments) ||
          Masked.isMasked(previousVersion.clientPolicyDocuments)) ?
          previousVersion.clientPolicyDocuments as MaskedContracts :
          this.createDocumentsRedline(
            ProposalField.ClientPolicies,
            ProposalField.ClientPolicyDocument,
            this.clientPolicyDocuments,
            previousVersion.clientPolicyDocuments,
            originalRedline?.clientPolicyDocuments
          ),
      vendorPolicyDocuments:
        (Masked.isMasked(this.vendorPolicyDocuments) ||
          Masked.isMasked(originalRedline?.vendorPolicyDocuments) ||
          Masked.isMasked(previousVersion.vendorPolicyDocuments)) ?
          previousVersion.vendorPolicyDocuments as MaskedContracts :
          this.createDocumentsRedline(
            ProposalField.VendorPolicies,
            ProposalField.VendorPolicyDocument,
            this.vendorPolicyDocuments,
            previousVersion.vendorPolicyDocuments,
            originalRedline?.vendorPolicyDocuments
          ),
      conflictsDocuments:
        (Masked.isMasked(this.conflictsDocuments) ||
          Masked.isMasked(originalRedline?.conflictsDocuments) ||
          Masked.isMasked(previousVersion.conflictsDocuments)) ?
          previousVersion.conflictsDocuments as MaskedContracts :
          this.createDocumentsRedline(
            ProposalField.Conflicts,
            ProposalField.ConflictsDocument,
            this.conflictsDocuments,
            previousVersion.conflictsDocuments,
            originalRedline?.conflictsDocuments
          ),
    };

    if (
      Masked.isNotMasked(conflictsCheckWaivedRedline) &&
      Masked.isNotMasked(documentsRedline.conflictsDocuments) &&
      conflictsCheckWaivedRedline.isAccepted &&
      conflictsCheckWaivedRedline.currentEntry?.isEqualTo(new AHBoolean(true))
    ) {
      documentsRedline.conflictsDocuments =
        documentsRedline.conflictsDocuments.acceptAll();
    }

    const datesRedline: RedlinedDates = {
      responseDueBy: this.createFieldRedline(
        ProposalField.ResponseDueBy,
        previousVersion.responseDueBy ?? null,
        this.responseDueBy ?? null,
        Date.Prototype,
        false,
        originalRedline?.responseDueBy
      ),
      startDate: this.createFieldRedline(
        ProposalField.StartDate,
        previousVersion.startDate ?? null,
        this.startDate ?? null,
        Date.Prototype,
        false,
        originalRedline?.startDate
      ),
      endDate: this.createFieldRedline(
        ProposalField.EndDate,
        previousVersion.endDate ?? null,
        this.endDate ?? null,
        Date.Prototype,
        false,
        originalRedline?.endDate
      )
    };

    const discountRedline =
      (Masked.isMasked(this.discount) ||
        Masked.isMasked(originalRedline?.discount) ||
        Masked.isMasked(previousVersion.discount)) ?
        previousVersion.discount as MaskedDiscount :
        this.createFieldRedline(
          ProposalField.Discount,
          previousVersion.discount ?? null,
          this.discount,
          Percent.Prototype,
          false,
          originalRedline?.discount
        );

    this._redlining = new ProposalRedline(
      nameRedline,
      descriptionRedline,
      datesRedline,
      teamRedline,
      feeScheduleRedline,
      conflictsCheckWaivedRedline,
      teamRestrictedRedline,
      documentsRedline,
      discountRedline,
      new Date()
    );
    this.updateLastSavedRedline();
  }

  private createFieldRedline<T extends IRedlineableField>(
    field: ProposalField,
    supersededProposalValue: T | null,
    currentProposalValue: T | null,
    prototype: T,
    diffWords: boolean,
    originalRedline?: FieldRedline<T>,
  ):
    FieldRedline<T> {
    const originalRedlineValue = supersededProposalValue ?? null;
    let revisedRedlineValue = currentProposalValue;
    let currentRedlineValue = undefined;

    // Invert the redlining to bluelining
    if (!
      this.isApprovedByBothParties && this.userIsLatestSubmittingParty
    ) {
      revisedRedlineValue = supersededProposalValue ?? null;
      currentRedlineValue = currentProposalValue;
    }

    let newRedline = new FieldRedline<T>(
      prototype,
      field,
      originalRedlineValue,
      revisedRedlineValue ?? null,
      undefined,
      diffWords,
      originalRedline
    );
    if (currentRedlineValue !== undefined) {
      newRedline = newRedline.edit(
        currentRedlineValue,
        true
      );
      newRedline.updateDiff()
    }
    newRedline.clearSessionHistory();

    if (originalRedline?.isCurrentWith(newRedline)) {
      return originalRedline;
    }

    return newRedline;
  }

  private createTeamRedline(
    previousTeam?: DetailedVendorTeam | MaskedTeam,
    originalRedlineArray?: FieldRedlineArray<Individual>
  ): FieldRedlineArray<Individual> {
    const thisMembers = Masked.isMasked(this.details?.vendorTeam)
      ? []
      : this.details?.vendorTeam?.members ?? [];
    const previousMembers = Masked.isMasked(previousTeam)
      ? []
      : previousTeam?.members ?? [];
    const newRedlineArray = new FieldRedlineArray<Individual>(
      Individual.Prototype,
      ProposalField.Team,
      ProposalField.TeamMember,
      (!this.isApprovedByBothParties ? previousMembers : thisMembers) ?? [],
      thisMembers ?? []
    );

    newRedlineArray.clearSessionHistory();
    return originalRedlineArray ? newRedlineArray.mergeOldRedlineArray(
      originalRedlineArray,
      this.userIsLatestSubmittingParty
    ) : newRedlineArray;
  }

  private createFeeScheduleRedline(
    previousFeeSchedule: FeeScheduleCategory[] | FeeScheduleCategoryMaskedCurrency[],
    originalRedline?: FeeScheduleRedline
  ) {
    if (Masked.isMasked(this.feeSchedule)) {
      throw new Error("Cannot create a fee schedule redline with a masked fee schedule.");
    }
    const newFeeScheduleRedline = new FeeScheduleRedline(
      (!this.isApprovedByBothParties ? previousFeeSchedule : this.feeSchedule) ?? [],
      this.feeSchedule
    );

    newFeeScheduleRedline.clearSessionHistory();
    return originalRedline ? newFeeScheduleRedline.mergeOldFeeScheduleRedline(
      originalRedline,
      this.userIsLatestSubmittingParty
    ) : newFeeScheduleRedline;
  }

  private createDocumentsRedline(
    arrayField: ProposalField,
    arrayEntryFieldConstructor: (id: Guid) => ProposalField,
    currentDocuments: WorkDocument[],
    previousDocuments?: WorkDocument[],
    originalRedlineArray?: FieldRedlineArray<WorkDocument>,
  ) {
    let newRedlineArray = new FieldRedlineArray<WorkDocument>(
      WorkDocument.Prototype,
      arrayField,
      arrayEntryFieldConstructor,
      (!this.isApprovedByBothParties ? previousDocuments : currentDocuments) ?? [],
      currentDocuments
    );

    newRedlineArray.clearSessionHistory();
    return originalRedlineArray ? newRedlineArray.mergeOldRedlineArray(
      originalRedlineArray,
      this.userIsLatestSubmittingParty,
    ) : newRedlineArray;
  }

  public updateLastSavedRedline() {
    this._lastSavedRedline = this._redlining?.clone();
  }

  public get hasUnsavedRedlineChanges(): boolean {
    if (!this._redlining && !this._lastSavedRedline) {
      return false;
    }
    if (this._redlining && !this._lastSavedRedline) {
      return true;
    }
    if (!this._redlining && this._lastSavedRedline) {
      return true;
    }

    return !this._redlining?.isEqualTo(this._lastSavedRedline);
  }
}

export interface ProposalJSON {
  currentUser: UserJSON;
  name?: string;
  description?: string;
  workAgreement: WorkAgreementJSON;
  negotiable: boolean;
  responseDueBy?: string;
  clientReviewers: ProposalReviewerJSON[];
  vendorReviewers: ProposalReviewerJSON[];
  RFPId?: string;
  id: string;
  createdDate?: string;
  lastUpdated?: string;
  creator: EntityRepresentativeJSON;
  creatorInfo?: UserInfoJSON;
  supersededById?: string;
  status: string;
  availableActions?: { [key in ProposalAction]?: string[] };
  replacedBy?: string;
  supersedes?: ProposalJSON;
  details?: ProposalDetailsJSON;
  redlining?: ProposalRedlineJSON;
  detailed?: boolean;
  originalProposal?: ProposalJSON;
  lastSavedRedline?: ProposalRedlineJSON;
}

export interface IProposalStore {
  createProposal(
    proposal: Proposal,
    session: Readonly<Session>
  ): Promise<Proposal>;

  saveProposal(updatedProposal: Proposal, proposalBuilderJSON?: object): Promise<{
    proposal: Proposal,
    userProposalBuilder?: ProposalBuilder,
    userRedlining?: ProposalRedline
  }>

  shareProposal(
    originalProposal: Proposal,
    updatedProposal: Proposal
  ): Promise<Proposal>;

  deleteProposal(proposal: Proposal): Promise<void>;

  hireProposal(proposal: Proposal): Promise<{
    proposal: Proposal,
    userProposalBuilder?: ProposalBuilder,
    userRedlining?: ProposalRedline
  }>;

  cancelProposal(proposal: Proposal): Promise<Proposal>;

  submitProposal(proposal: Proposal): Promise<{
    proposal: Proposal,
    userProposalBuilder?: ProposalBuilder,
    userRedlining?: ProposalRedline
  }>;

  approveProposal(proposal: Proposal, session: Readonly<Session>): Promise<{
    proposal: Proposal,
    userProposalBuilder?: ProposalBuilder,
    userRedlining?: ProposalRedline
  }>;

  rejectProposal(proposal: Proposal): Promise<{
    proposal: Proposal,
    userProposalBuilder?: ProposalBuilder,
    userRedlining?: ProposalRedline
  }>;

  requestRevision(
    proposal: Proposal,
    session: Readonly<Session>
  ): Promise<{
    proposal: Proposal,
    userProposalBuilder?: ProposalBuilder,
    userRedlining?: ProposalRedline
  }>;
}

export type CompleteProposalSpec = {
  RFPId?: Guid;
  vendors: EntityVendorRepresentative[];
  teamRestricted: AHBoolean | MaskedTeam;
  feeSchedule: FeeScheduleCategory[] | FeeScheduleCategoryMaskedCurrency[] | MaskedFeeSchedule;
  conflictsCheckWaived: AHBoolean | MaskedContracts;
  conflictsDocuments: WorkDocument[] | MaskedContracts;
  clientPolicyDocuments: WorkDocument[] | MaskedContracts;
  vendorPolicyDocuments: WorkDocument[] | MaskedContracts;
  clientTeamTemplateIds: Guid[];
  vendorTeamTemplateIds: Guid[];
  clientFeeScheduleTemplateIds: Guid[];
  vendorFeeScheduleTemplateIds: Guid[];
  discount: Percent | MaskedDiscount;
  vendorTeam?: DetailedVendorTeam | MaskedTeam;
  client?: EntityClientRepresentative;
  name?: ProjectName;
  description?: ProjectDescription;
  negotiable: boolean;
  responseDueBy?: Date;
  startDate?: Date;
  endDate?: Date;
  clientReviewers?: ProposalReviewer[];
  vendorReviewers?: ProposalReviewer[];
};

export type ProposalMetaInfo = {
  RFPId?: Guid;
  id: Guid;
  workAgreementId?: Guid;
  createdDate: Date;
  lastUpdated: Date;
  creator: EntityRepresentative;
  creatorInfo?: UserInfo;
  status: ProposalStatus;
  supersededById?: Guid;
  availableActions: { [key in ProposalAction]: Guid[] };
  replacedBy?: Guid;
  hireDate?: Date;
  archiveDate?: Date;
};

export type ProposalDetails = {
  client: Individual;
  vendorTeam?: DetailedVendorTeam | MaskedTeam;
  redlining?: ProposalRedline;
  redliningUpdated?: Date;
  supersedes?: Proposal;
  supersededById?: Guid;
};

export type FreelyPatchableFields = {
  reviewers: ProposalReviewer[];
  teamTemplateIds: Guid[];
  feeScheduleTemplateIds: Guid[];
};

export enum ProposalFieldCategory {
  Details = "Details",
  Team = "Team",
  FeeSchedule = "Fee Schedule",
  Conflicts = "Conflicts",
  Policies = "Policies",
  Discount = "Discount",
}

export interface ProposalDetailsJSON {
  client?: IndividualJSON;
  team?: DetailedVendorTeamJSON | MaskedField;
  redlining?: ProposalRedlineJSON;
  redliningUpdated?: string;
  supersedes?: ProposalJSON;
  supersededById?: string;
}

export interface ProposalFieldJSON {
  name: string;
  category: string;
  id?: string;
  commentField?: ProposalFieldJSON | "general";
}

export class ProposalField {
  static readonly General: ProposalField = new ProposalField(
    ProposalFieldName.General,
    ProposalFieldCategory.Details
  );
  static readonly Client: ProposalField = new ProposalField(
    ProposalFieldName.Client,
    ProposalFieldCategory.Details
  );
  static readonly Name: ProposalField = new ProposalField(
    ProposalFieldName.Name,
    ProposalFieldCategory.Details
  );
  static readonly Description: ProposalField = new ProposalField(
    ProposalFieldName.Description,
    ProposalFieldCategory.Details
  );
  static readonly Negotiable: ProposalField = new ProposalField(
    ProposalFieldName.Negotiable,
    ProposalFieldCategory.Details
  );
  static readonly ResponseDueBy: ProposalField = new ProposalField(
    ProposalFieldName.ResponseDueBy,
    ProposalFieldCategory.Details
  );
  static readonly StartDate: ProposalField = new ProposalField(
    ProposalFieldName.StartDate,
    ProposalFieldCategory.Details
  );
  static readonly EndDate: ProposalField = new ProposalField(
    ProposalFieldName.EndDate,
    ProposalFieldCategory.Details
  );

  static readonly Team: ProposalField = new ProposalField(
    ProposalFieldName.Team,
    ProposalFieldCategory.Team
  );
  static readonly TeamRestriction: ProposalField = new ProposalField(
    ProposalFieldName.TeamRestriction,
    ProposalFieldCategory.Team,
    undefined,
    ProposalField.Team
  );
  static readonly TeamLeader: ProposalField = new ProposalField(
    ProposalFieldName.TeamLeader,
    ProposalFieldCategory.Team,
    undefined,
    ProposalField.Team
  );
  static readonly FeeSchedule: ProposalField = new ProposalField(
    ProposalFieldName.FeeSchedule,
    ProposalFieldCategory.FeeSchedule
  );
  static readonly Conflicts: ProposalField = new ProposalField(
    ProposalFieldName.Conflicts,
    ProposalFieldCategory.Conflicts
  );
  static readonly WaiveConflictsCheck: ProposalField = new ProposalField(
    ProposalFieldName.WaiveConflictsCheck,
    ProposalFieldCategory.Conflicts,
    undefined,
    ProposalField.Conflicts
  );
  static readonly Policies: ProposalField = new ProposalField(
    ProposalFieldName.Policies,
    ProposalFieldCategory.Policies
  );
  static readonly ClientPolicies: ProposalField = new ProposalField(
    ProposalFieldName.ClientPolicies,
    ProposalFieldCategory.Policies
  );
  static readonly VendorPolicies: ProposalField = new ProposalField(
    ProposalFieldName.VendorPolicies,
    ProposalFieldCategory.Policies
  );
  static readonly Discount: ProposalField = new ProposalField(
    ProposalFieldName.Discount,
    ProposalFieldCategory.Discount
  );
  public readonly name: ProposalFieldName;
  public readonly category: ProposalFieldCategory;
  public readonly id: Guid | undefined;
  private readonly _commentFieldOverride?: ProposalField;

  private constructor(
    name: ProposalFieldName,
    category: ProposalFieldCategory,
    id?: Guid,
    commentField?: ProposalField
  ) {
    this.name = name;
    this.category = category;
    this.id = id;
    this._commentFieldOverride = commentField;
  }

  public get key(): string {
    let key = `${this.commentField}.${this.name}`;
    if (this.id) {
      key = `${key}.${this.id}`;
    }
    return key;
  }

  public get commentField(): ProposalField {
    return this._commentFieldOverride ?? this;
  }

  static TeamMember(id: Guid): ProposalField {
    return new ProposalField(
      ProposalFieldName.Team,
      ProposalFieldCategory.Team,
      id,
      ProposalField.Team
    );
  }

  static FeeScheduleCategory(id: Guid): ProposalField {
    return new ProposalField(
      ProposalFieldName.FeeSchedule,
      ProposalFieldCategory.FeeSchedule,
      id,
      ProposalField.FeeSchedule
    );
  }

  static ConflictsDocument(id: Guid): ProposalField {
    return new ProposalField(
      ProposalFieldName.Conflicts,
      ProposalFieldCategory.Conflicts,
      id,
      ProposalField.Conflicts
    );
  }

  static ClientPolicyDocument(id: Guid): ProposalField {
    return new ProposalField(
      ProposalFieldName.ClientPolicies,
      ProposalFieldCategory.Policies,
      id,
      ProposalField.ClientPolicies
    );
  }

  static VendorPolicyDocument(id: Guid): ProposalField {
    return new ProposalField(
      ProposalFieldName.VendorPolicies,
      ProposalFieldCategory.Policies,
      id,
      ProposalField.VendorPolicies
    );
  }

  public static fromJSON(json: ProposalFieldJSON | "general"): ProposalField {
    let commentField: ProposalField | undefined;
    if (json === "general") {
      return ProposalField.General;
    } else if (json.commentField) {
      commentField = ProposalField.fromJSON(json.commentField);
    }

    const toTitleCase = (str: string): string => {
      return str
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    };

    // Find the ProposalFieldCategory enum key by value
    const categoryKey = Object.keys(ProposalFieldCategory).find(
      key => ProposalFieldCategory[key as keyof typeof ProposalFieldCategory] === json.category
    ) as keyof typeof ProposalFieldCategory;

    return new ProposalField(
      ProposalFieldName[toTitleCase(json.name) as keyof typeof ProposalFieldName],
      ProposalFieldCategory[categoryKey],
      json.id ? Guid.fromJSON(json.id) : undefined,
      commentField
    );
  }

  public toJSON(): ProposalFieldJSON {
    return {
      name: this.name.toString(),
      category: this.category.toString(),
      id: this.id?.toJSON(),
      commentField: this._commentFieldOverride?.toJSON(),
    };
  }

  public isEqualTo(field?: ProposalField | null): boolean {
    if (!field) return false;
    return (
      this.name === field.name &&
      (this.id?.isEqualTo(field.id) || (!this.id && !field.id))
    );
  }

  public toString(): string {
    return this.name.toString();
  }
}
